import { z } from 'zod';
/**
 * `market_ua` — AUTO.RIA comparables for a lot, as the ingest bot sends them
 * (`marketUa` on POST /vehicles/ingest, stored verbatim on alpicar_cars).
 *
 * The ingest contract keeps the block opaque (`z.record(z.unknown())`) so a bot
 * that adds a field never gets a 400. THIS schema is the read side: the lot
 * page parses the stored block with `parseMarketUa` and renders only what it
 * proves is there. Every schema is `.passthrough()` for the same reason — the
 * bot owns the shape, we own what we draw.
 *
 * Money is USD (`currency: "USD"`), prices are whole dollars.
 */
export const MarketUaComparableSchema = z
    .object({
    id: z.number().int().nullish(),
    link: z.string().nullish(),
    name: z.string().nullish(),
    title: z.string().nullish(),
    /** "Mercedes-Benz GLC-Class 2025 • C254 • 200 9G-Tronic … • 15 тис. км" */
    details: z.string().nullish(),
    subtitle: z.string().nullish(),
    year: z.number().int().nullish(),
    price: z.number().positive(),
    priceDisplay: z.string().nullish(),
    mileageKm: z.number().nullish(),
    /** Listing photo on the AUTO.RIA CDN (cdn*.riastatic.com), supplied by the bot. */
    photo: z.string().nullish(),
})
    .passthrough();
export const MarketUaYearFilterSchema = z
    .object({
    applied: z.boolean().nullish(),
    lotYear: z.number().int().nullish(),
    from: z.number().int().nullish(),
    to: z.number().int().nullish(),
})
    .passthrough();
export const MarketUaSchema = z
    .object({
    status: z.string(),
    source: z.string().nullish(),
    currency: z.string().nullish(),
    /** ISO datetime of the AUTO.RIA search. */
    fetchedAt: z.string().nullish(),
    searchUrl: z.string().nullish(),
    /** Listings the search matched (the headline number). */
    count: z.number().int().nonnegative().nullish(),
    /** Priced listings the stats are computed from. */
    usedCount: z.number().int().nonnegative().nullish(),
    minPrice: z.number().nullish(),
    avgPrice: z.number().nullish(),
    medianPrice: z.number().nullish(),
    maxPrice: z.number().nullish(),
    yearFilter: MarketUaYearFilterSchema.nullish(),
    min: MarketUaComparableSchema.nullish(),
    median: MarketUaComparableSchema.nullish(),
    max: MarketUaComparableSchema.nullish(),
})
    .passthrough();
/**
 * Narrow a stored `market_ua` to something the lot page can draw, or `null`.
 *
 * `null` for anything short of a complete answer — a failed search, a missing
 * slot, a non-positive price, or a min/median/max that are out of order. The
 * card hides itself on `null`; a half-drawn comparison is worse than none.
 */
export function parseMarketUa(raw) {
    const parsed = MarketUaSchema.safeParse(raw);
    if (!parsed.success)
        return null;
    const block = parsed.data;
    if (block.status !== 'ok' || !block.min || !block.median || !block.max)
        return null;
    if (block.min.price > block.median.price || block.median.price > block.max.price)
        return null;
    return block;
}
/** Fewest priced listings a median must rest on before it may move the customs base. */
export const MIN_MARKET_COMPARABLES = 3;
/**
 * The AUTO.RIA median a lot's customs value may be estimated from, or `null`.
 *
 * Stricter than {@link parseMarketUa}: the comparables card can draw three
 * listings, but a median that moves the client's headline price has to rest on
 * at least {@link MIN_MARKET_COMPARABLES} priced ones. A block without
 * `usedCount` is rejected rather than trusted — we cannot tell one listing
 * from forty.
 *
 * `medianPrice` (the stats over every priced listing) wins; the median slot's
 * own price is the fallback for a bot that only sent the slot.
 */
export function resolveMarketRetailUsd(raw) {
    const block = parseMarketUa(raw);
    if (!block)
        return null;
    const usedCount = block.usedCount;
    if (typeof usedCount !== 'number' || usedCount < MIN_MARKET_COMPARABLES)
        return null;
    const medianUsd = block.medianPrice ?? block.median.price;
    if (!Number.isFinite(medianUsd) || medianUsd <= 0)
        return null;
    return { medianUsd, usedCount, fetchedAt: block.fetchedAt ?? null };
}
//# sourceMappingURL=market-ua.js.map